/**
 * ModuleSubscribeDialog + useModuleSubscribe
 *
 * Hook + componente autônomo para o modal de contratação de módulo.
 * Pode ser usado em qualquer página (settings, module-locked-view, etc.).
 */
import {useCallback, useState} from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import {MODULE_GROUPS} from "../feature-module-map";
import {moduleBillingService, type BillingModuleId, type ModuleBundleQuote, type ModuleQuote} from "../module-billing.service";
import type {ModuleSubscriptionSummary} from "../module-billing.service";
import {StripePaymentDialog} from "./stripe-payment-dialog";

// ─── Visual tokens (idênticos ao settings.page.tsx) ──────────────────────────
const SYMPL_GRADIENT = "linear-gradient(135deg, #0f766e 0%, #0d9488 40%, #14b8a6 100%)";
const SYMPL_GRADIENT_SOFT = "linear-gradient(180deg, rgba(13, 148, 136, 0.12) 0%, rgba(241, 245, 249, 0.95) 100%)";
const MODAL_REVEAL = "sympl-modal-reveal 260ms cubic-bezier(0.2, 0, 0, 1)";
const REVIEW_CARD_HOVER = {
  transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 14px 34px rgba(15, 23, 42, 0.10)",
  },
};

const formatMoney = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

const getVariantById = (moduleId: BillingModuleId) => {
  for (const group of MODULE_GROUPS) {
    const variant = group.variants.find((item) => item.id === moduleId);
    if (variant) return {group, variant};
  }
  return null;
};

/** Variação “canônica” do grupo (Stripe > startedAt > preço), inclui cancel_at_period_end. */
const pickCanonicalVariantInGroup = (
  group: (typeof MODULE_GROUPS)[number],
  summary: ModuleSubscriptionSummary | null
): BillingModuleId | null => {
  if (!summary) return null;
  const variants = group.variants.filter((v) => {
    const st = summary.modules[v.id]?.status;
    return st === "active" || st === "cancel_at_period_end" || st === "pending_payment";
  });
  if (variants.length === 0) return null;
  return [...variants].sort((a, b) => {
    const aIn = summary.modules[a.id]?.inStripeSubscription ? 1 : 0;
    const bIn = summary.modules[b.id]?.inStripeSubscription ? 1 : 0;
    if (aIn !== bIn) return bIn - aIn;
    const sa = (summary.modules[a.id] as {startedAt?: number} | undefined)?.startedAt || 0;
    const sb = (summary.modules[b.id] as {startedAt?: number} | undefined)?.startedAt || 0;
    if (sa !== sb) return sb - sa;
    return b.monthlyPrice - a.monthlyPrice;
  })[0].id;
};

const variantWithScheduledCancelInGroup = (
  group: (typeof MODULE_GROUPS)[number],
  summary: ModuleSubscriptionSummary | null
): BillingModuleId | null => {
  if (!summary) return null;
  const candidates = group.variants.filter((v) => summary.modules[v.id]?.status === "cancel_at_period_end");
  if (candidates.length === 0) return null;
  return [...candidates].sort((a, b) => {
    const aIn = summary.modules[a.id]?.inStripeSubscription ? 1 : 0;
    const bIn = summary.modules[b.id]?.inStripeSubscription ? 1 : 0;
    if (aIn !== bIn) return bIn - aIn;
    const sa = (summary.modules[a.id] as {startedAt?: number} | undefined)?.startedAt || 0;
    const sb = (summary.modules[b.id] as {startedAt?: number} | undefined)?.startedAt || 0;
    if (sa !== sb) return sb - sa;
    return b.monthlyPrice - a.monthlyPrice;
  })[0].id;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function replaceModuleInSameGroup(current: BillingModuleId[], incoming: BillingModuleId): BillingModuleId[] {
  const incomingGroup = MODULE_GROUPS.find((g) => g.variants.some((v) => v.id === incoming));
  if (!incomingGroup) return Array.from(new Set([...current, incoming]));
  const next = current.filter((id) => !incomingGroup.variants.some((v) => v.id === id));
  return Array.from(new Set([...next, incoming]));
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export type UseModuleSubscribeReturn = {
  dialogProps: ModuleSubscribeDialogProps;
  open: (groupId: string, summary: ModuleSubscriptionSummary | null) => void;
  toast: {open: boolean; severity: "success" | "error"; message: string};
  onToastClose: () => void;
  reload: () => void;
};

type ReloadFn = () => void | Promise<void>;

export function useModuleSubscribe(reloadSubscription: ReloadFn): UseModuleSubscribeReturn {
  const [modalOpen, setModalOpen] = useState(false);
  const [summary, setSummary] = useState<ModuleSubscriptionSummary | null>(null);
  const [modalStep, setModalStep] = useState<"module_details" | "plan_review">("module_details");
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<BillingModuleId | null>(null);
  const [selectedModuleIds, setSelectedModuleIds] = useState<BillingModuleId[]>([]);
  const [quote, setQuote] = useState<ModuleQuote | null>(null);
  const [bundleQuote, setBundleQuote] = useState<ModuleBundleQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState<{clientSecret: string; amountBrl: number; pendingModuleIds: BillingModuleId[]} | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState<{
    open: boolean;
    activatedModuleIds: BillingModuleId[];
    amountPaid: number;
    nextChargeAmount: number;
    nextChargeAt: number | null;
  } | null>(null);
  const [extrasExpanded, setExtrasExpanded] = useState(false);
  const [scheduledCancelVariantId, setScheduledCancelVariantId] = useState<BillingModuleId | null>(null);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [toast, setToast] = useState<{open: boolean; severity: "success" | "error"; message: string}>({
    open: false,
    severity: "success",
    message: "",
  });

  const open = useCallback(async (groupId: string, summary: ModuleSubscriptionSummary | null) => {
    const group = MODULE_GROUPS.find((g) => g.id === groupId);
    if (!group) return;
    setSummary(summary);

    const cancelVariant = variantWithScheduledCancelInGroup(group, summary);
    setScheduledCancelVariantId(cancelVariant);

    const canonical = pickCanonicalVariantInGroup(group, summary);
    const initialVariant = (
      canonical ??
      group.variants.find((v) => v.monthlyPrice === 0)?.id ??
      group.variants[0].id
    ) as BillingModuleId;
    const currentEnabled = summary
      ? (Object.keys(summary.modules).filter((id) => {
          const st = summary.modules[id]?.status;
          return st === "active" || st === "cancel_at_period_end";
        }) as BillingModuleId[])
      : [];
    const initialSelection = replaceModuleInSameGroup(currentEnabled, initialVariant);

    setSelectedGroupId(groupId);
    setSelectedVariantId(initialVariant);
    setSelectedModuleIds(initialSelection);
    setModalStep("module_details");
    setExtrasExpanded(false);
    setModalOpen(true);

    if (cancelVariant) {
      setQuote(null);
      setBundleQuote(null);
      setQuoteLoading(false);
      return;
    }

    setQuoteLoading(true);
    try {
      const [singleQuote, packageQuote] = await Promise.all([
        moduleBillingService.quote(initialVariant),
        moduleBillingService.quoteBundle(initialSelection),
      ]);
      setQuote(singleQuote);
      setBundleQuote(packageQuote);
    } catch (e) {
      setModalOpen(false);
      console.error(e);
      setToast({open: true, severity: "error", message: "Não foi possível carregar os detalhes."});
    } finally {
      setQuoteLoading(false);
    }
  }, []);

  const revokeScheduledCancel = useCallback(async () => {
    if (!scheduledCancelVariantId) return;
    setRevokeLoading(true);
    try {
      await moduleBillingService.stripeRevokeModuleScheduledCancel(scheduledCancelVariantId);
      await reloadSubscription();
      setModalOpen(false);
      setScheduledCancelVariantId(null);
      setToast({open: true, severity: "success", message: "Agendamento cancelado. Seu plano segue ativo na assinatura."});
    } catch (e) {
      setToast({
        open: true,
        severity: "error",
        message: e instanceof Error ? e.message : "Não foi possível reativar o módulo.",
      });
    } finally {
      setRevokeLoading(false);
    }
  }, [scheduledCancelVariantId, reloadSubscription]);

  const onVariantChange = useCallback(async (variantId: BillingModuleId) => {
    const nextSelection = replaceModuleInSameGroup(selectedModuleIds, variantId);
    setSelectedVariantId(variantId);
    setSelectedModuleIds(nextSelection);
    setQuoteLoading(true);
    try {
      const [singleQuote, packageQuote] = await Promise.all([
        moduleBillingService.quote(variantId),
        moduleBillingService.quoteBundle(nextSelection),
      ]);
      setQuote(singleQuote);
      setBundleQuote(packageQuote);
    } catch (e) {
      setToast({open: true, severity: "error", message: e instanceof Error ? e.message : "Erro ao recalcular."});
    } finally {
      setQuoteLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModuleIds]);

  const toggleSuggested = useCallback(async (moduleId: BillingModuleId, checked: boolean) => {
    const next = checked
      ? replaceModuleInSameGroup(selectedModuleIds, moduleId)
      : selectedModuleIds.filter((id) => id !== moduleId);
    setSelectedModuleIds(next);
    setQuoteLoading(true);
    try {
      const packageQuote = await moduleBillingService.quoteBundle(next);
      setBundleQuote(packageQuote);
    } catch (e) {
      setToast({open: true, severity: "error", message: "Erro ao recalcular."});
    } finally {
      setQuoteLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModuleIds]);

  const activate = useCallback(async () => {
    if (!selectedVariantId || selectedModuleIds.length === 0) return;
    setConfirmLoading(true);
    try {
      const checkout = await moduleBillingService.stripeCreateModuleBundleCheckout(selectedModuleIds);
      if (checkout.requiresPayment && checkout.clientSecret) {
        const activeSet = new Set(
          Object.entries(summary?.modules || {})
            .filter(([, value]) => value.status === "active")
            .map(([id]) => id as BillingModuleId)
        );
        const pendingModuleIds = selectedModuleIds.filter((moduleId) => {
          const variantInfo = getVariantById(moduleId);
          const monthlyPrice = variantInfo?.variant.monthlyPrice || 0;
          return monthlyPrice > 0 && !activeSet.has(moduleId);
        });
        setPaymentDialog({
          clientSecret: checkout.clientSecret,
          amountBrl: bundleQuote?.amountDueNowTotal || 0,
          pendingModuleIds,
        });
        setModalOpen(false);
        return;
      }
      await reloadSubscription();
      setModalOpen(false);
      setToast({
        open: true,
        severity: "success",
        message: checkout.status === "scheduled"
          ? "Alteração agendada: o downgrade passa a valer no próximo ciclo de cobrança."
          : "Módulos atualizados com sucesso.",
      });
    } catch (e) {
      setToast({open: true, severity: "error", message: e instanceof Error ? e.message : "Falha ao contratar."});
    } finally {
      setConfirmLoading(false);
    }
  }, [selectedVariantId, selectedModuleIds, bundleQuote, reloadSubscription, summary?.modules]);

  const handlePaymentSuccess = useCallback(async () => {
    const paidAmount = paymentDialog?.amountBrl || 0;
    const activatedModules = paymentDialog?.pendingModuleIds || [];
    try {
      const overview = await moduleBillingService.stripeGetPlanOverview().catch(() => null);
      await reloadSubscription();
      setPaymentDialog(null);
      setPaymentSuccess({
        open: true,
        activatedModuleIds: activatedModules,
        amountPaid: paidAmount,
        nextChargeAmount: overview?.nextChargeAmount || 0,
        nextChargeAt: overview?.nextChargeAt || null,
      });
    } catch (e) {
      setPaymentDialog(null);
      setToast({
        open: true,
        severity: "error",
        message: e instanceof Error ? e.message : "Pagamento ok, mas falhou ao atualizar a tela. Atualize a página.",
      });
    }
  }, [paymentDialog?.amountBrl, paymentDialog?.pendingModuleIds, reloadSubscription]);

  return {
    dialogProps: {
      open: modalOpen,
      modalStep,
      selectedGroupId,
      selectedVariantId,
      selectedModuleIds,
      quote,
      bundleQuote,
      quoteLoading,
      confirmLoading,
      extrasExpanded,
      summary,
      paymentDialog,
      paymentSuccess,
      onClose: () => {
        setModalOpen(false);
        setModalStep("module_details");
        setExtrasExpanded(false);
        setScheduledCancelVariantId(null);
      },
      onPaymentClose: () => setPaymentDialog(null),
      onPaymentSuccess: handlePaymentSuccess,
      onSuccessClose: () => setPaymentSuccess(null),
      onVariantChange,
      onToggleSuggested: toggleSuggested,
      onExtrasToggle: () => setExtrasExpanded((v) => !v),
      onGoReview: () => setModalStep("plan_review"),
      onGoBack: () => setModalStep("module_details"),
      onActivate: () => void activate(),
      scheduledCancelVariantId,
      revokeLoading,
      onRevokeScheduledCancel: () => void revokeScheduledCancel(),
    },
    open,
    toast,
    onToastClose: () => setToast((t) => ({...t, open: false})),
    reload: () => void reloadSubscription(),
  };
}

// ─── Dialog Component ─────────────────────────────────────────────────────────
export type ModuleSubscribeDialogProps = {
  open: boolean;
  modalStep: "module_details" | "plan_review";
  selectedGroupId: string | null;
  selectedVariantId: BillingModuleId | null;
  selectedModuleIds: BillingModuleId[];
  quote: ModuleQuote | null;
  bundleQuote: ModuleBundleQuote | null;
  quoteLoading: boolean;
  confirmLoading: boolean;
  extrasExpanded: boolean;
  summary: ModuleSubscriptionSummary | null;
  paymentDialog: {clientSecret: string; amountBrl: number; pendingModuleIds: BillingModuleId[]} | null;
  paymentSuccess: {
    open: boolean;
    activatedModuleIds: BillingModuleId[];
    amountPaid: number;
    nextChargeAmount: number;
    nextChargeAt: number | null;
  } | null;
  onClose: () => void;
  onPaymentClose: () => void;
  onPaymentSuccess: () => void | Promise<void>;
  onSuccessClose: () => void;
  onVariantChange: (variantId: BillingModuleId) => void;
  onToggleSuggested: (moduleId: BillingModuleId, checked: boolean) => void;
  onExtrasToggle: () => void;
  onGoReview: () => void;
  onGoBack: () => void;
  onActivate: () => void;
  scheduledCancelVariantId: BillingModuleId | null;
  revokeLoading: boolean;
  onRevokeScheduledCancel: () => void;
};

export function ModuleSubscribeDialog(props: ModuleSubscribeDialogProps) {
  const {
    open, modalStep, selectedGroupId, selectedVariantId, selectedModuleIds,
    quote, bundleQuote, quoteLoading, confirmLoading, extrasExpanded, summary, paymentDialog,
    paymentSuccess, onClose, onPaymentClose, onPaymentSuccess, onSuccessClose, onVariantChange, onToggleSuggested, onExtrasToggle,
    onGoReview, onGoBack, onActivate,
    scheduledCancelVariantId, revokeLoading, onRevokeScheduledCancel,
  } = props;
  const theme = useTheme();
  const activeModuleEntries = summary
    ? Object.entries(summary.modules).filter(([, value]) => value.status === "active")
    : [];
  const currentPlanMonthlyTotal = activeModuleEntries.reduce((sum, [, value]) => sum + value.monthlyPrice, 0);
  const currentEnabledSet = new Set(activeModuleEntries.map(([id]) => id));
  const selectionMatchesCurrentPlan =
    selectedModuleIds.length === currentEnabledSet.size &&
    selectedModuleIds.every((id) => currentEnabledSet.has(id));
  const monthlyDeltaAfterConfirm =
    bundleQuote ? bundleQuote.finalMonthlyTotal - currentPlanMonthlyTotal : null;

  const findModuleDisplay = (moduleId: BillingModuleId) => getVariantById(moduleId);

  const activePlanTitles = activeModuleEntries.map(([, value]) => {
    const display = findModuleDisplay(value.moduleId);
    return display ? `${display.group.name} — ${display.variant.name}` : value.moduleId;
  });

  const bundleQuoteChangingLines = bundleQuote?.lines.filter((line) => !line.alreadyEnabled) ?? [];

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={modalStep === "plan_review" ? "md" : "sm"}
      PaperProps={{
        sx: modalStep === "plan_review" ? {
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${alpha("#0d9488", 0.35)}`,
          boxShadow: "0 24px 64px rgba(15, 23, 42, 0.12)",
          animation: MODAL_REVEAL,
          "@keyframes sympl-modal-reveal": {
            from: { opacity: 0, transform: "translateY(10px) scale(0.985)" },
            to: { opacity: 1, transform: "translateY(0) scale(1)" },
          },
        } : {},
      }}
    >
      {/* ─── Title ─── */}
      <DialogTitle
        sx={modalStep === "plan_review" ? {
          background: SYMPL_GRADIENT,
          color: "primary.contrastText",
          py: 2.25, pr: 2, pl: 2,
          boxShadow: "inset 0 -1px 0 rgba(255,255,255,0.12)",
        } : { py: 2 }}
      >
        {modalStep === "module_details" ? (
          <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: "-0.02em" }}>
            Escolha do módulo
          </Typography>
        ) : (
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <AutoAwesomeIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.95, fontWeight: 800, letterSpacing: "0.18em", lineHeight: 1.2 }}>Symplou</Typography>
              <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: "-0.02em", lineHeight: 1.25 }}>Resumo da contratação</Typography>
            </Box>
          </Stack>
        )}
      </DialogTitle>

      {/* ─── Content ─── */}
      <DialogContent
        dividers
        sx={modalStep === "plan_review" ? {
          bgcolor: "#f8fafc",
          backgroundImage: `linear-gradient(180deg, ${alpha("#0d9488", 0.07)} 0%, transparent 140px)`,
        } : undefined}
      >
        {scheduledCancelVariantId ? (
          <Stack spacing={2}>
            <Alert severity="warning" variant="outlined">
              Este módulo está <strong>marcado para encerrar no fim do ciclo atual</strong>. Você não pode trocar a
              variação enquanto isso estiver agendado — apenas manter o plano ou aguardar o encerramento.
            </Alert>
            {(() => {
              const display = getVariantById(scheduledCancelVariantId);
              return display ? (
                <Typography variant="body1">
                  Plano atual: <strong>{display.group.name}</strong> — <strong>{display.variant.name}</strong>
                </Typography>
              ) : null;
            })()}
          </Stack>
        ) : quoteLoading || !quote ? (
          <Typography variant="body2" color="text.secondary">Carregando detalhes...</Typography>
        ) : modalStep === "module_details" ? (
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={800}>{quote.moduleName}</Typography>
            <Typography variant="body2" color="text.secondary">{quote.moduleDescription}</Typography>

            {selectedGroupId && (
              <RadioGroup
                value={selectedVariantId || ""}
                onChange={(e) => onVariantChange(e.target.value as BillingModuleId)}
              >
                {MODULE_GROUPS.find((g) => g.id === selectedGroupId)?.variants.map((variant) => (
                  <FormControlLabel
                    key={variant.id}
                    value={variant.id}
                    control={<Radio />}
                    label={`${variant.name} - ${variant.monthlyPrice === 0 ? "Gratuito" : `${formatMoney(variant.monthlyPrice)}/mês`}`}
                  />
                ))}
              </RadioGroup>
            )}

            {quote.currentModuleId && quote.currentModuleId !== quote.moduleId ? (
              <Alert severity="warning" variant="outlined" sx={{ borderColor: alpha(theme.palette.warning.main, 0.45) }}>
                <Typography variant="body2" fontWeight={800}>Troca de variação</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Plano atual neste produto: <strong>{quote.currentModuleName || quote.currentModuleId}</strong>
                  {" → "}
                  nova seleção: <strong>{quote.moduleName}</strong>
                </Typography>
              </Alert>
            ) : null}

            {/* Alerta contextual por tipo */}
            {quote.changeType === "upgrade" && (
              <Alert severity="success" variant="outlined" sx={{ borderColor: alpha(theme.palette.success.main, 0.4) }}>
                <strong>Upgrade imediato.</strong>
                {quote.currentModuleName ? (
                  <> Partindo de <strong>{quote.currentModuleName}</strong>, </>
                ) : null}
                {" "}você pagará apenas a diferença proporcional de{" "}
                <strong>{formatMoney(quote.amountDueNow)}</strong> até{" "}
                {new Date(quote.currentPeriodEnd).toLocaleDateString("pt-BR")}. A partir do próximo ciclo o valor será{" "}
                <strong>{formatMoney(quote.fullMonthlyAmount)}/mês</strong>.
              </Alert>
            )}
            {quote.changeType === "downgrade" && (
              <Alert severity="info" variant="outlined" sx={{ borderColor: alpha(theme.palette.info.main, 0.4) }}>
                <strong>Downgrade no próximo ciclo.</strong> Você mantém o plano atual (e o valor já pago) até{" "}
                <strong>{new Date(quote.currentPeriodEnd).toLocaleDateString("pt-BR")}</strong>.
                {" "}Não há cobrança extra nem reembolso agora; a mensalidade menor passa a valer na renovação, como no
                cancelamento ao fim do período.
              </Alert>
            )}
            {quote.changeType === "new" && (
              <Alert severity="info" variant="outlined" sx={{ borderColor: alpha(theme.palette.primary.main, 0.35) }}>
                Pró-rata deste módulo (até {new Date(quote.currentPeriodEnd).toLocaleDateString("pt-BR")}):
                {" "}<strong>{formatMoney(quote.amountDueNow)}</strong>.
                O valor final com desconto de pacote aparece no <strong>resumo Symplou</strong> no passo seguinte.
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.6 }}>
              Opcional: você pode ampliar o pacote com outros módulos para ativar <strong>desconto progressivo</strong>.
            </Typography>

            {quote.suggestions.length > 0 ? (
              <>
                <Button
                  fullWidth size="large" variant="outlined" color="primary"
                  onClick={onExtrasToggle}
                  startIcon={<LocalOfferIcon />}
                  endIcon={<ExpandMoreIcon sx={{ transform: extrasExpanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />}
                  sx={{
                    py: 1.25, borderWidth: 2, borderStyle: "solid", borderColor: "primary.main",
                    bgcolor: extrasExpanded ? alpha(theme.palette.primary.main, 0.08) : "transparent",
                    fontWeight: 800,
                    "&:hover": { borderWidth: 2, bgcolor: alpha(theme.palette.primary.main, 0.1) },
                  }}
                >
                  Adicionar mais módulos ao pacote — ganhe mais desconto
                </Button>
                <Collapse in={extrasExpanded}>
                  <Paper elevation={0} sx={{ mt: 1.5, p: 2, borderRadius: 3, background: SYMPL_GRADIENT_SOFT, border: `1px dashed ${alpha(theme.palette.primary.main, 0.45)}` }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
                      <WorkspacePremiumIcon color="primary" sx={{ mt: 0.25 }} />
                      <Box>
                        <Typography variant="subtitle1" fontWeight={900} color="primary.dark">Promoção em pacote Symplou</Typography>
                        <Typography variant="body2" color="text.secondary">Cada módulo extra aumenta o desconto sobre o conjunto.</Typography>
                        {bundleQuote && (
                          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
                            <Chip icon={<LocalOfferIcon sx={{ "&&": { fontSize: 18 } }} />} label={`${bundleQuote.discountPct}% OFF no pacote`} color="primary" sx={{ fontWeight: 800, px: 0.5 }} />
                            <Chip label={`Pró-rata hoje: ${formatMoney(bundleQuote.amountDueNowTotal)}`} variant="outlined" sx={{ fontWeight: 700, borderColor: alpha(theme.palette.primary.main, 0.5) }} />
                            <Chip label={`Pacote: ${formatMoney(bundleQuote.finalMonthlyTotal)}/mês`} variant="outlined" sx={{ fontWeight: 700, borderColor: alpha(theme.palette.primary.main, 0.5) }} />
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                    <Grid container spacing={1.5}>
                      {quote.suggestions.map((item) => {
                        const checked = selectedModuleIds.includes(item.id);
                        return (
                          <Grid item xs={12} sm={6} key={item.id}>
                            <Card elevation={0} onClick={() => onToggleSuggested(item.id, !checked)} sx={{
                              height: "100%", cursor: "pointer", border: 2, borderStyle: "solid",
                              borderColor: checked ? "primary.main" : "divider",
                              bgcolor: checked ? alpha(theme.palette.primary.main, 0.1) : "background.paper",
                              boxShadow: checked ? `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}` : "none",
                              transition: "border-color 0.15s, box-shadow 0.15s, background 0.15s",
                            }}>
                              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                  <Checkbox checked={checked} tabIndex={-1} disableRipple color="primary" inputProps={{ "aria-hidden": true }} sx={{ p: 0, mr: 1, pointerEvents: "none" }} />
                                  <Chip size="small" label={checked ? "No pacote" : "Toque para somar"} color={checked ? "primary" : "default"} sx={{ fontWeight: 800 }} />
                                </Stack>
                                <Typography variant="subtitle1" fontWeight={900} sx={{ mt: 1 }}>{item.name}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, minHeight: 36 }}>{item.description}</Typography>
                                <Typography variant="h6" color="primary.dark" sx={{ fontWeight: 900, mt: 1 }}>
                                  {item.monthlyPrice === 0 ? "Grátis" : `${formatMoney(item.monthlyPrice)}/mês`}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Paper>
                </Collapse>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">Não há outros módulos sugeridos para combinar neste fluxo.</Typography>
            )}

            {bundleQuote && !extrasExpanded && (
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.04), borderColor: alpha(theme.palette.primary.main, 0.2) }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Prévia rápida: desconto <strong>{bundleQuote.discountPct}%</strong> · pró-rata total{" "}
                  <strong>{formatMoney(bundleQuote.amountDueNowTotal)}</strong> · pacote{" "}
                  <strong>{formatMoney(bundleQuote.finalMonthlyTotal)}</strong>/mês
                </Typography>
              </Paper>
            )}
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            {bundleQuote ? (
              <Paper elevation={0} sx={{p: 2.5, borderRadius: 3, border: `1px solid ${alpha("#0d9488", 0.35)}`, background: SYMPL_GRADIENT_SOFT}}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 2}}>
                  <LocalOfferIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight={900} color="primary.dark">Resumo financeiro</Typography>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Paper variant="outlined" sx={{p: 2, borderRadius: 2, height: "100%", bgcolor: "background.paper"}}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>A pagar agora</Typography>
                      <Typography variant="h5" fontWeight={900} sx={{mt: 0.5}}>{formatMoney(bundleQuote.amountDueNowTotal)}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{mt: 1, display: "block"}}>Pró-rata e ajustes até o fim do ciclo atual.</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper variant="outlined" sx={{p: 2, borderRadius: 2, height: "100%", bgcolor: "background.paper"}}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>Mensalidade após confirmar</Typography>
                      <Typography variant="h5" fontWeight={900} sx={{mt: 0.5}}>
                        {formatMoney(bundleQuote.finalMonthlyTotal)}
                        <Box component="span" sx={{fontSize: "0.95rem", fontWeight: 600, color: "text.secondary", ml: 0.25}}>/mês</Box>
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{mt: 1, display: "block"}}>Valor do pacote com desconto progressivo.</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper variant="outlined" sx={{p: 2, borderRadius: 2, height: "100%", bgcolor: "background.paper"}}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>Desconto no pacote</Typography>
                      <Typography variant="h5" fontWeight={900} sx={{mt: 0.5}}>{bundleQuote.discountPct}%</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{mt: 1, display: "block"}}>Dia de cobrança (âncora): dia <strong>{bundleQuote.anchorDay}</strong>.</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            ) : null}

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 1.5}}>
                <CheckCircleOutlineIcon color="action" />
                <Typography variant="h6" fontWeight={800}>Plano atual (resumo)</Typography>
              </Stack>
              {activeModuleEntries.length === 0 ? (
                <Alert severity="info" variant="outlined">Nenhum módulo pago ativo no momento — você está montando um pacote novo.</Alert>
              ) : (
                <Paper variant="outlined" sx={{p: 2, borderRadius: 2, ...REVIEW_CARD_HOVER}}>
                  <Typography variant="body2" color="text.secondary">
                    O que já entra na sua assinatura hoje (só totais — o detalhe por módulo fica em Meu plano).
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={900} sx={{mt: 1.25}}>
                    {activeModuleEntries.length}{" "}
                    {activeModuleEntries.length === 1 ? "módulo ativo" : "módulos ativos"} ·{" "}
                    {formatMoney(currentPlanMonthlyTotal)}/mês
                  </Typography>
                  {activePlanTitles.length <= 3 ? (
                    <Typography variant="caption" color="text.secondary" sx={{display: "block", mt: 1, lineHeight: 1.6}}>
                      {activePlanTitles.join(" · ")}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{display: "block", mt: 1, lineHeight: 1.6}}>
                      Vários módulos já compõem o pacote; abaixo, “O que muda” mostra o efeito desta contratação.
                    </Typography>
                  )}
                </Paper>
              )}
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 1.5}}>
                <SwapHorizIcon color="primary" />
                <Typography variant="h6" fontWeight={800}>O que muda na assinatura</Typography>
              </Stack>
              {bundleQuote ? (
                bundleQuoteChangingLines.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum módulo novo ou troca de plano nesta confirmação — o resumo financeiro acima reflete desconto de pacote sobre o que você já tem.
                  </Typography>
                ) : (
                  <Stack spacing={1.25}>
                    {bundleQuoteChangingLines.map((line) => {
                      const display = findModuleDisplay(line.moduleId);
                      return (
                        <Paper
                          key={line.moduleId}
                          variant="outlined"
                          sx={{
                            p: 1.75,
                            borderRadius: 2,
                            borderColor: alpha(theme.palette.primary.main, 0.45),
                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                            ...REVIEW_CARD_HOVER,
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <Box sx={{mt: 0.25, color: "primary.main"}}>
                              <SwapHorizIcon fontSize="small" />
                            </Box>
                            <Box sx={{flex: 1}}>
                              <Typography variant="subtitle2" fontWeight={900}>
                                {display ? `${display.group.name} · ${display.variant.name}` : line.moduleName}
                              </Typography>
                              {line.fromModuleName ? (
                                <Typography variant="caption" color="text.secondary" sx={{display: "block", mt: 0.35}}>
                                  Substitui <strong>{line.fromModuleName}</strong> no que você tem hoje neste produto.
                                </Typography>
                              ) : null}
                              <Typography variant="caption" color="text.secondary" sx={{display: "block", mt: 0.25}}>
                                {line.description}
                              </Typography>
                              <Stack direction="row" flexWrap="wrap" gap={1} sx={{mt: 1.25}}>
                                <Chip size="small" label={`Pró-rata: ${formatMoney(line.amountDueNow)}`} variant="outlined" sx={{fontWeight: 700}} />
                                <Chip size="small" label={`Mês: ${formatMoney(line.monthlyPriceWithDiscount)}`} sx={{fontWeight: 700}} />
                              </Stack>
                            </Box>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                )
              ) : null}
            </Box>

            {monthlyDeltaAfterConfirm !== null && !selectionMatchesCurrentPlan ? (
              <Paper variant="outlined" sx={{p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.06), borderColor: alpha(theme.palette.info.main, 0.35)}}>
                <Typography variant="subtitle2" fontWeight={900} color="info.dark">Impacto na mensalidade</Typography>
                <Typography variant="body2" color="text.secondary" sx={{mt: 0.75}}>
                  Hoje o pacote ativo soma <strong>{formatMoney(currentPlanMonthlyTotal)}/mês</strong>.
                  Após confirmar: <strong>{formatMoney(bundleQuote?.finalMonthlyTotal || 0)}/mês</strong>
                  {monthlyDeltaAfterConfirm > 0 ? (
                    <> (aumento de <strong>{formatMoney(monthlyDeltaAfterConfirm)}</strong>).</>
                  ) : monthlyDeltaAfterConfirm < 0 ? (
                    <> (redução de <strong>{formatMoney(-monthlyDeltaAfterConfirm)}</strong>).</>
                  ) : (
                    <> (sem alteração no total mensal).</>
                  )}
                </Typography>
              </Paper>
            ) : null}

            {selectionMatchesCurrentPlan ? (
              <Alert severity="info">Sua seleção coincide com os módulos já ativos.</Alert>
            ) : null}
          </Stack>
        )}
      </DialogContent>

      {/* ─── Actions ─── */}
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {scheduledCancelVariantId ? (
          <>
            <Button onClick={onClose} color="inherit" disabled={revokeLoading}>Fechar</Button>
            <Button
              variant="contained"
              disabled={revokeLoading}
              onClick={onRevokeScheduledCancel}
              sx={{ fontWeight: 800, px: 3, borderRadius: 2, background: SYMPL_GRADIENT }}
            >
              {revokeLoading ? "Salvando..." : "Manter plano (cancelar agendamento)"}
            </Button>
          </>
        ) : modalStep === "module_details" ? (
          <>
            <Button onClick={onClose} color="inherit">Cancelar</Button>
            <Button
              variant="contained"
              disabled={!quote || quoteLoading}
              onClick={onGoReview}
              sx={{ fontWeight: 800, px: 3, borderRadius: 2, background: SYMPL_GRADIENT }}
            >
              Revisar e contratar
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onGoBack} color="inherit" disabled={confirmLoading}>Voltar</Button>
            <Button
              variant="contained"
              disabled={confirmLoading || selectionMatchesCurrentPlan}
              onClick={onActivate}
              sx={{ fontWeight: 800, px: 3, borderRadius: 2, background: SYMPL_GRADIENT }}
            >
              {confirmLoading ? "Contratando..." : "Confirmar contratação"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
      <StripePaymentDialog
        open={Boolean(paymentDialog)}
        clientSecret={paymentDialog?.clientSecret || ""}
        amountBrl={paymentDialog?.amountBrl || 0}
        expectedModuleIds={paymentDialog?.pendingModuleIds}
        title="Pagamento da contratação"
        description="Finalize o pagamento seguro para ativar os módulos pagos selecionados."
        onSuccess={onPaymentSuccess}
        onClose={onPaymentClose}
      />
      <Dialog open={Boolean(paymentSuccess?.open)} onClose={onSuccessClose} fullWidth maxWidth="sm">
        <DialogTitle>Pagamento confirmado</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Alert severity="success">
              Pagamento de <strong>{formatMoney(paymentSuccess?.amountPaid || 0)}</strong> aprovado com sucesso.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Módulos ativados nesta cobrança:
            </Typography>
            <Stack spacing={0.5}>
              {(paymentSuccess?.activatedModuleIds || []).map((moduleId) => {
                const display = findModuleDisplay(moduleId);
                return (
                  <Typography key={moduleId} variant="body2">
                    - {display ? `${display.group.name} · ${display.variant.name}` : moduleId}
                  </Typography>
                );
              })}
            </Stack>
            <Alert severity="info" variant="outlined">
              Próxima fatura: <strong>{formatMoney(paymentSuccess?.nextChargeAmount || 0)}</strong>
              {paymentSuccess?.nextChargeAt ? (
                <> em <strong>{new Date(paymentSuccess.nextChargeAt).toLocaleDateString("pt-BR")}</strong>.</>
              ) : "."}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={onSuccessClose}>Entendi</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Toast helper (reutilizável ao lado do dialog) ────────────────────────────
export function ModuleSubscribeToast({
  open,
  severity,
  message,
  onClose,
}: {
  open: boolean;
  severity: "success" | "error";
  message: string;
  onClose: () => void;
}) {
  return (
    <Snackbar open={open} autoHideDuration={5000} onClose={onClose} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
      <Alert onClose={onClose} severity={severity} variant="filled" sx={{ borderRadius: 3, fontWeight: 600 }}>
        {message}
      </Alert>
    </Snackbar>
  );
}
